<template>
  <div id="app" :class="{ 'dark-theme': themeState.isDarkMode }">
    <!-- <notifications></notifications> -->
    <router-view />
  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      themeState: {
        isDarkMode: false
      }
    };
  },
  provide() {
    return {
      themeState: this.themeState,
      toggleTheme: this.toggleTheme
    };
  },
  methods: {
    applyTheme(isDarkMode) {
      this.themeState.isDarkMode = isDarkMode;
      const root = document.documentElement;
      const body = document.body;

      root.classList.toggle('dark-theme', isDarkMode);
      root.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
      body.classList.toggle('dark-theme', isDarkMode);
      localStorage.setItem('schooldock-theme', isDarkMode ? 'dark' : 'light');
    },
    toggleTheme() {
      this.applyTheme(!this.themeState.isDarkMode);
    }
  },
  mounted() {
    const savedTheme = localStorage.getItem('schooldock-theme');
    this.applyTheme(savedTheme === 'dark');
  }
};
</script>

<style lang="scss">
:root {
  --app-radius-sm: 0.5rem;
  --app-radius: 0.75rem;
  --app-radius-lg: 1rem;
  --app-focus: rgba(37, 99, 235, 0.28);
  --app-font: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color-scheme: light;
  --motion-fast: 160ms;
  --motion-base: 240ms;
  --motion-slow: 360ms;
  --motion-ease: cubic-bezier(0.22, 0.61, 0.36, 1);
  --app-bg: #f4f7fb;
  --app-surface: #ffffff;
  --app-surface-alt: #f8fafc;
  --app-surface-strong: #f1f5f9;
  --app-text: #0f172a;
  --app-muted: #64748b;
  --app-border: rgba(15, 23, 42, 0.08);
  --app-shadow: rgba(15, 23, 42, 0.08);
  --header-bg: linear-gradient(87deg, #1850b8 0%, #3b7be3 100%);
  --header-text: #ffffff;
  --sidebar-bg: #ffffff;
  --sidebar-text: #64748b;
  --sidebar-text-strong: #0f172a;
  --sidebar-hover-bg: rgba(15, 23, 42, 0.03);
  --sidebar-icon-bg: rgba(15, 23, 42, 0.05);
  --sidebar-active-bg: linear-gradient(90deg, rgba(24, 80, 184, 0.16), rgba(24, 80, 184, 0.08));
  --sidebar-active-text: #1850b8;
  --sidebar-badge-bg: #f8fafc;
  --sidebar-badge-text: #64748b;
  --content-bg: #f8fafc;
  --card-bg: #ffffff;
  --input-bg: #ffffff;
  --table-bg: #ffffff;
  --table-header-bg: #f8fafc;
}

:root.dark-theme {
  color-scheme: dark;
  --app-bg: #07111f;
  --app-surface: #101826;
  --app-surface-alt: #121c2b;
  --app-surface-strong: #162338;
  --app-text: #f8fafc;
  --app-muted: #94a3b8;
  --app-border: rgba(255, 255, 255, 0.12);
  --app-shadow: rgba(2, 6, 23, 0.36);
  --header-bg: linear-gradient(87deg, #1850b8 0%, #2b56a9 100%);
  --header-text: #f8fbff;
  --sidebar-bg: #0f172a;
  --sidebar-text: #cbd5e1;
  --sidebar-text-strong: #f8fafc;
  --sidebar-hover-bg: rgba(255, 255, 255, 0.06);
  --sidebar-icon-bg: rgba(255, 255, 255, 0.08);
  --sidebar-active-bg: linear-gradient(90deg, rgba(24, 80, 184, 0.24), rgba(24, 80, 184, 0.16));
  --sidebar-active-text: #8fb0f0;
  --sidebar-badge-bg: rgba(255, 255, 255, 0.08);
  --sidebar-badge-text: #cbd5e1;
  --content-bg: #0b1220;
  --card-bg: #111b2d;
  --input-bg: #111b2d;
  --table-bg: #111b2d;
  --table-header-bg: #162338;
}

body {
  font-family: var(--app-font);
  font-size: 0.925rem;
  line-height: 1.5;
  background-color: var(--app-bg) !important;
  background-image: radial-gradient(circle at top left, rgba(24, 80, 184, 0.14), transparent 32%), linear-gradient(180deg, rgba(255,255,255,0.02), transparent 60%);
  color: var(--app-text) !important;
  transition: background-color 0.25s ease, color 0.25s ease;
}

html,
body,
#app,
.wrapper,
.main-content,
.content {
  min-height: 100%;
}

#app,
.wrapper,
.main-content,
.content {
  background-color: transparent;
  color: var(--app-text);
}

#app {
  min-height: 100vh;
  overflow-x: hidden;
}

.wrapper {
  min-height: 100vh;
  overflow-x: hidden;
}

.main-content {
  min-height: 100vh;
  padding: 1rem 1.1rem 2rem;
  background: transparent;
  overflow-x: hidden;
  min-width: 0;
}

/* Shared surface and control styling for all routed areas. */
.main-content .card,
.modal-content,
.dropdown-menu {
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius);
}

.main-content .card-header,
.main-content .card-footer {
  background: transparent;
  border-color: var(--app-border);
}

h1, h2, h3, h4, h5, h6 {
  letter-spacing: -0.018em;
  line-height: 1.3;
}

.form-control,
.custom-select,
.input-group-text {
  min-height: 2.5rem;
  border-radius: var(--app-radius-sm);
}

.btn {
  min-height: 2.35rem;
  border-radius: var(--app-radius-sm);
  font-weight: 600;
}

.btn:active:not(:disabled) {
  transform: translateY(1px);
}

.pagination {
  flex-wrap: wrap;
  gap: 0.2rem;
}

.pagination .page-link {
  min-width: 2.35rem;
  min-height: 2.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--app-radius-sm);
}

.table {
  color: var(--app-text);
  font-size: 0.875rem;
  font-variant-numeric: tabular-nums;
}

.table thead th {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.035em;
  vertical-align: middle;
}

.table th,
.table td {
  padding: 0.75rem;
  vertical-align: middle;
  overflow-wrap: anywhere;
}

.table td .btn,
.table td button,
.table td .dropdown {
  white-space: nowrap;
}

.table-responsive,
.base-table-scroll {
  max-width: 100%;
  border-radius: var(--app-radius-sm);
  -webkit-overflow-scrolling: touch;
}

.table-responsive > .table,
.base-table-scroll > .table {
  margin-bottom: 0;
}

@media (max-width: 767.98px) {
  .main-content .row {
    min-width: 0;
  }

  .main-content [class*="col-"] {
    min-width: 0;
  }

  .table:not(.table-responsive) {
    display: block;
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .table:not(.table-responsive) > thead,
  .table:not(.table-responsive) > tbody,
  .table:not(.table-responsive) > tfoot {
    min-width: max-content;
  }

  .table th,
  .table td {
    padding: 0.65rem 0.55rem;
  }

  .card-body {
    min-width: 0;
  }

  .modal-dialog {
    margin: 0.5rem;
  }

  .modal-content {
    max-height: calc(100dvh - 1rem);
    overflow-y: auto;
  }
}

@media (max-width: 575.98px) {
  .main-content {
    padding-right: 0.65rem;
    padding-left: 0.65rem;
  }

  .main-content .card-body {
    padding: 1rem;
  }

  .main-content .d-flex:not(.btn-group) {
    min-width: 0;
  }

  .main-content .form-inline,
  .main-content .input-group {
    max-width: 100%;
  }

  .main-content .btn-group {
    flex-wrap: wrap;
  }
}

@media (max-width: 767.98px) {
  .main-content {
    padding: 0.75rem 0.65rem 1.5rem;
  }
}

@supports (padding: max(0px)) {
  .navbar-top {
    padding-top: 0.5rem !important;
    padding-top: calc(0.5rem + env(safe-area-inset-top)) !important;
  }
}

.navbar,
.navbar-light,
.navbar-collapse,
.navbar-vertical,
.content-footer,
.card,
.modal-content,
.dropdown-menu,
.table,
.form-control,
.form-control:focus,
.custom-select,
.custom-file-label,
.input-group-text,
.input-group-prepend,
.input-group-append,
.bg-white,
.bg-light,
.bg-default {
  background-color: var(--app-surface) !important;
  color: var(--app-text) !important;
  border-color: var(--app-border) !important;
}

.bg-secondary,
.bg-gradient-primary,
.bg-gradient-success,
.bg-gradient-info,
.bg-gradient-warning,
.bg-gradient-danger {
  background-color: var(--app-surface-strong) !important;
  color: var(--app-text) !important;
}

.table thead th {
  background-color: var(--table-header-bg) !important;
  color: var(--app-text) !important;
}

.table td,
.table th {
  border-color: var(--app-border) !important;
}

.navbar-light .navbar-nav .nav-link,
.navbar-light .navbar-brand {
  color: var(--app-text) !important;
}

.card,
.modal-content,
.dropdown-menu {
  box-shadow: 0 10px 30px var(--app-shadow);
}

.modal.show {
  z-index: 3000 !important;
}

.swal2-container.schooldock-alert-container {
  z-index: 11000 !important;
}

.schooldock-alert-popup {
  width: 28rem !important;
  max-width: calc(100vw - 2rem) !important;
  border: 1px solid var(--app-border) !important;
  border-radius: 14px !important;
  background: var(--app-surface) !important;
  color: var(--app-text) !important;
  box-shadow: 0 18px 45px var(--app-shadow) !important;
}

.schooldock-alert-title {
  color: var(--app-text) !important;
  font-size: 1.1rem !important;
}

.schooldock-alert-message {
  color: var(--app-muted) !important;
  line-height: 1.55 !important;
}

.schooldock-alert-confirm,
.schooldock-alert-cancel {
  min-width: 6.5rem;
  border-radius: 8px !important;
  font-weight: 600 !important;
}

.schooldock-alert-cancel {
  color: var(--app-text) !important;
  background: var(--app-surface-strong) !important;
}

@media (max-width: 575.98px) {
  .swal2-container.schooldock-alert-container {
    padding: 1rem !important;
  }

  .schooldock-alert-popup {
    margin: 0 !important;
  }
}

body.modal-open .main-content {
  position: relative;
  z-index: 2000 !important;
}

body.modal-open .dashboard-navbar .nav-item.dropdown {
  visibility: hidden !important;
  pointer-events: none !important;
}

/* Shared motion language for the dashboard surface. */
.main-content .card {
  animation: surfaceReveal var(--motion-slow) var(--motion-ease) both;
  transition: box-shadow var(--motion-base) var(--motion-ease),
    border-color var(--motion-base) ease;
}

.main-content .card:hover {
  box-shadow: 0 14px 34px var(--app-shadow);
}

.main-content .card:nth-child(2) {
  animation-delay: 40ms;
}

.main-content .card:nth-child(3) {
  animation-delay: 80ms;
}

.main-content .card:nth-child(4) {
  animation-delay: 120ms;
}

button,
a,
.nav-link,
.dropdown-item,
.form-control,
.custom-select,
.btn {
  transition: color var(--motion-fast) ease,
    background-color var(--motion-fast) ease,
    border-color var(--motion-fast) ease,
    box-shadow var(--motion-fast) ease,
    transform var(--motion-fast) var(--motion-ease);
}

button:focus-visible,
a:focus-visible,
select:focus-visible,
input:focus-visible,
textarea:focus-visible {
  outline: 3px solid rgba(24, 80, 184, 0.32);
  outline-offset: 2px;
}

.dropdown-menu {
  transform-origin: top right;
  transition: opacity var(--motion-fast) ease, transform var(--motion-fast) var(--motion-ease);
}

.dropdown-menu:not(.show) {
  opacity: 0;
  transform: translateY(-0.35rem) scale(0.98);
  pointer-events: none;
}

.dropdown-menu.show {
  opacity: 1;
  transform: translateY(0) scale(1);
}

@keyframes surfaceReveal {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
  }
}
</style>
